/* eslint-disable @typescript-eslint/naming-convention */

import { Children, useEffect, useState } from 'react';

import { useDetermineMobile } from '@myrealtrip/device-detect';
import DomainManager from '../../../shared/utils/DomainManager';

import styled from '@emotion/styled';
import { LocalNavVerticalItem } from '../../../constants/lnb';
import { useLayoutHeaderContext } from '../../../modules/LayoutHeaderContext';

import LocalNavCarousel from './LocalNavCarousel';
import LocalNavItem from './LocalNavItem';
import RestLocalNavItems from './RestLocalNavItems';
import { useEnvContext } from '../../../modules/EnvProvider';
import { isActiveMenu } from '../../utils/isActiveMenu';

const MAX_DISPLAY_VERTICAL_LENGTH = 9;

type NormalizedLocalNavVerticalItem = LocalNavVerticalItem & {
  determinedMenuUrl: string;
};

export default function DefaultLocalNavItems() {
  const [initialSlideIndex, setInitialSlideIndex] = useState(0);
  const { lnbVerticalItems, isFlightMode } = useLayoutHeaderContext();
  const isMobile = useDetermineMobile();
  const { targetEnv } = useEnvContext();
  const domainManager = new DomainManager(targetEnv);

  const homeMenu: LocalNavVerticalItem = {
    id: 0,
    code: 'home',
    title: '홈',
    position: 0,
    level: 1,
    lnb_required_badge: false,
    link: {
      desktop: domainManager.getTravelerServiceDomain(),
      mobile_web: domainManager.getTravelerServiceDomain(),
    },
  };

  const mergedVerticalItems = [homeMenu, ...lnbVerticalItems].sort(
    (a, b) => a.position - b.position,
  );
  const normalizedVerticalItems: NormalizedLocalNavVerticalItem[] = mergedVerticalItems.map(
    (item) => {
      const { link } = item;
      const determinedMenuUrl = isMobile ? link.mobile_web : link.desktop;

      return { ...item, determinedMenuUrl };
    },
  );
  const displayVerticalItems = normalizedVerticalItems.slice(0, MAX_DISPLAY_VERTICAL_LENGTH);
  const restVerticalItems = normalizedVerticalItems
    .slice(displayVerticalItems.length)
    .map(({ link, title }) => ({
      link: link.desktop,
      label: title,
    }));

  useEffect(() => {
    setInitialSlideIndex(
      normalizedVerticalItems.findIndex(({ code, link }) =>
        isActiveMenu({ code, menuUrl: isMobile ? link.mobile_web : link.desktop, isFlightMode }),
      ),
    );
  }, [normalizedVerticalItems, isMobile, isFlightMode]);

  return (
    <Container>
      {isMobile && (
        <LocalNavCarousel initialSlideIndex={initialSlideIndex}>
          {Children.toArray(
            normalizedVerticalItems?.map(
              ({ title, code, determinedMenuUrl, lnb_required_badge }) => (
                <LocalNavItem
                  label={title}
                  code={code}
                  link={determinedMenuUrl}
                  showNewBadge={lnb_required_badge}
                />
              ),
            ),
          )}
        </LocalNavCarousel>
      )}
      {!isMobile && (
        <>
          {Children.toArray(
            displayVerticalItems?.map(({ title, code, determinedMenuUrl, lnb_required_badge }) => (
              <LocalNavItem
                label={title}
                code={code}
                link={determinedMenuUrl}
                showNewBadge={lnb_required_badge}
              />
            )),
          )}
          {restVerticalItems.length > 0 && <RestLocalNavItems verticalItems={restVerticalItems} />}
        </>
      )}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  justify-content: space-between;
`;
